import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { Users, Gavel, Wallet, ArrowRight, ChevronRight } from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const tracks = {
  citizen: {
    label: "Citizen",
    icon: Users,
    intro: "From confusion to resolution. Free preliminary guidance + matched lawyer + tracked case.",
    steps: [
      { t: "Register or sign in", d: "Email, phone OTP or Google. Tell us your location and preferred language." },
      { t: "Describe your legal issue", d: "Plain language is fine — Justice-AI handles the translation to legal terms." },
      { t: "Receive preliminary guidance", d: "Get jurisdiction-aware advice, required documents and next steps." },
      { t: "Get matched with a lawyer", d: "Uber-style matching by expertise, location, language and urgency." },
      { t: "Book consultation", d: "Virtual, phone or in-person. Calendar invites delivered to both sides." },
      { t: "Engage and pay", d: "Engagement letter generated. Stage 1 payment held in escrow." },
      { t: "Track your case", d: "Live status, documents, milestones and lawyer messages on your dashboard." },
      { t: "Rate the outcome", d: "Your feedback powers the matching engine and protects future clients." },
    ],
  },
  lawyer: {
    label: "Lawyer",
    icon: Gavel,
    intro: "Verified clients. AI productivity. Escrow-protected payments. Reputation that compounds.",
    steps: [
      { t: "Apply and get verified", d: "Submit Bar credentials, practice areas, court jurisdictions and languages." },
      { t: "Set availability and rates", d: "Calendar sync. Fee bands. Languages spoken. Preferences honored." },
      { t: "Receive matched requests", d: "Matched matters arrive with urgency, location, fee band and Match Score." },
      { t: "Use the AI Copilot", d: "Research, drafting, case strategy and document review built-in." },
      { t: "Hold consultation", d: "Confirm matter suitability and propose fee structure + timeline." },
      { t: "Manage active cases", d: "Filing, hearings, documents, deadlines — all in your dashboard." },
      { t: "Get paid in stages", d: "Stage 1 (20-30%) → Stage 2 (50-60%) → Stage 3 (10-20%). Escrow-released." },
      { t: "Build reputation", d: "Ratings, response time and outcomes drive your visibility and referrals." },
    ],
  },
  donor: {
    label: "Donor / NGO",
    icon: Wallet,
    intro: "Fund the Justice Wallet. Sponsor cases. Track impact. SDG 16 aligned.",
    steps: [
      { t: "Choose a justice category", d: "Women, youth, rural, human rights, public-interest or specific case types." },
      { t: "Fund the wallet", d: "Bank, card or institutional transfer. Funds enter a transparent escrow." },
      { t: "Eligibility automation", d: "AI vetting + admin approval per beneficiary. NGO referrals honored." },
      { t: "Verified lawyer assigned", d: "Same matching engine. Same standards. Different funding source." },
      { t: "Milestone-based release", d: "Funds flow only as deliverables are completed — no leakage." },
      { t: "Live impact dashboard", d: "Beneficiaries, demographics, outcomes and SDG 16 metrics — in real time." },
      { t: "Annual audit & report", d: "Independent audit and downloadable donor reports for compliance." },
    ],
  },
};

const HowItWorks = () => {
  const [active, setActive] = useState("citizen");
  const cur = tracks[active];

  return (
    <div className="bg-ink text-white" data-testid="how-it-works-page">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={22} /></div>
        <ScaleWatermark />
        <div className="turquoise-aura -top-32 right-0 opacity-50" />
        <div className="grain-overlay" />
        <div className="container relative z-10 mx-auto px-6 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="aj-chip mb-6">How It Works</div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
              One platform.{" "}
              <span className="italic text-aj-gradient">Three journeys.</span>{" "}
              <span className="text-gold-gradient">Zero friction.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <Section>
        <div className="flex flex-wrap gap-3 mb-12">
          {Object.entries(tracks).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`hiw-page-tab-${key}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-montserrat text-xs uppercase tracking-[0.16em] border transition-all ${
                active === key
                  ? "bg-turquoise text-ink border-turquoise shadow-[0_0_24px_rgba(0,200,215,0.35)]"
                  : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10"
        >
          <p className="font-cormorant italic text-2xl md:text-3xl text-white/85 max-w-3xl leading-snug">
            {cur.intro}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4">
          {cur.steps.map((s, i) => (
            <motion.div
              key={`${active}-${i}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <div className="glass rounded-2xl p-6 flex items-start gap-5 hover:border-turquoise/30 transition-all">
                <div className="font-playfair italic text-4xl text-gold-gradient leading-none shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="font-playfair text-xl text-white mb-1">{s.t}</h3>
                  <p className="font-inter text-sm text-white/60 leading-relaxed">{s.d}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section className="border-t border-white/8">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Ready to start?</Eyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-8 leading-tight">
            Justice begins with{" "}
            <span className="italic text-aj-gradient">a single step.</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ai-assistant" data-testid="hiw-cta-help" className="btn-aj-primary">
              Talk to Justice-AI <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/for-lawyers" data-testid="hiw-cta-lawyer" className="btn-aj-secondary">
              For Lawyers
            </Link>
            <Link to="/for-ngos" data-testid="hiw-cta-donor" className="btn-aj-secondary">
              For Donors
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HowItWorks;
